/**
 * Regime State Widget
 * Displays current market regime classification and confidence
 */

import { useEffect, useState } from 'react';
import { fetchRegimeState, type RegimeState } from '../services/forexService';
import { TrendingUp, TrendingDown, Activity, HelpCircle } from 'lucide-react';
import { safeFormatTimestamp } from '../lib/accountFormatters';

export function RegimeStateWidget() {
  const [regimeState, setRegimeState] = useState<RegimeState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadRegimeState = async () => {
      try {
        const data = await fetchRegimeState();
        setRegimeState(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load regime state');
      } finally {
        setLoading(false);
      }
    };

    loadRegimeState();
    const interval = setInterval(loadRegimeState, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-neutral-800 rounded mb-2"></div>
          <div className="h-10 w-full bg-neutral-800 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !regimeState) {
    return (
      <div className="rounded-lg border border-red-900 bg-red-950/20 p-4">
        <div className="text-red-400 text-sm">
          {error || 'Regime state unavailable'}
        </div>
      </div>
    );
  }

  const getRegimeColor = (state: string) => {
    switch (state) {
      case 'trending':
        return 'text-green-400 border-green-900 bg-green-950/20';
      case 'ranging':
        return 'text-yellow-400 border-yellow-900 bg-yellow-950/20';
      case 'volatile':
        return 'text-red-400 border-red-900 bg-red-950/20';
      default:
        return 'text-neutral-400 border-neutral-800 bg-neutral-900';
    }
  };

  const getRegimeIcon = (state: string) => {
    switch (state) {
      case 'trending':
        return <TrendingUp className="w-5 h-5" />;
      case 'ranging':
        return <Activity className="w-5 h-5" />;
      case 'volatile':
        return <TrendingDown className="w-5 h-5" />;
      default:
        return <HelpCircle className="w-5 h-5" />;
    }
  };

  const regimeColor = getRegimeColor(regimeState.regime_state);
  const regimeIcon = getRegimeIcon(regimeState.regime_state);

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-neutral-400">Market Regime</h3>
        <span className="text-xs text-neutral-500">
          {regimeState.strategy_type || 'N/A'}
        </span>
      </div>

      <div className={`rounded-md border p-3 ${regimeColor}`}>
        <div className="flex items-center gap-3">
          {regimeIcon}
          <div className="flex-1">
            <div className="font-semibold capitalize">{regimeState.regime_state}</div>
            <div className="text-xs opacity-80">
              Confidence: {(regimeState.regime_confidence * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {regimeState.last_signal_time && (
        <div className="text-xs space-y-1 text-neutral-400">
          <div className="flex justify-between">
            <span>Last Signal:</span>
            <span className={regimeState.last_signal_direction === 'long' ? 'text-green-400' : 'text-red-400'}>
              {regimeState.last_signal_direction?.toUpperCase() || 'N/A'}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Signal Confidence:</span>
            <span>{((regimeState.last_signal_confidence || 0) * 100).toFixed(1)}%</span>
          </div>
          <div className="text-neutral-500 text-[10px]">
            {safeFormatTimestamp(regimeState.last_signal_time, { fallback: '—', invalidFallback: '—' })}
          </div>
        </div>
      )}

      <div className="text-[10px] text-neutral-600 pt-2 border-t border-neutral-800">
        Profile: {regimeState.active_profile}
      </div>
    </div>
  );
}
